<script setup>
/**
 * Historial INLINE dentro del hilo del chat (Victor 2026-06-03 · Propuesta 2).
 * Muestra, ARRIBA de los mensajes de la conversacion actual, todos los mensajes
 * de las conversaciones ANTERIORES del mismo cliente (misma sucursal), en orden
 * y solo lectura, con una marca sutil "conversacion · fecha" donde empieza cada
 * hilo viejo. Asi se lee todo lo hablado de corrido, en la misma bandeja, sin
 * botones ni ventanas. NO mueve ni borra nada (usa el endpoint full_history).
 */
import { ref, watch } from 'vue';
import ConversationApi from 'dashboard/api/conversations';

const props = defineProps({
  // display_id de la conversacion actual (la que muestra la lista en vivo abajo)
  conversationId: { type: [Number, String], default: null },
});
const emit = defineEmits(['loaded']);

const items = ref([]);

const isOutgoing = m => m.message_type === 1 || m.message_type === 3;
const fmtTime = ts =>
  new Date(ts * 1000).toLocaleString('es-DO', {
    hour: '2-digit',
    minute: '2-digit',
  });
const convDate = ts =>
  new Date(ts * 1000).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const load = async () => {
  items.value = [];
  if (!props.conversationId) return;
  try {
    const { data } = await ConversationApi.fullHistory(props.conversationId);
    const all = data.payload || [];
    // Excluir los mensajes de la conversacion ACTUAL (ya los pinta la lista en vivo).
    const older = all.filter(
      m => String(m.conversation_id) !== String(props.conversationId)
    );
    const built = [];
    let lastConv = null;
    older.forEach(m => {
      if (m.conversation_id !== lastConv) {
        lastConv = m.conversation_id;
        built.push({
          kind: 'sep',
          id: `sep-${m.conversation_id}`,
          label: convDate(m.created_at),
        });
      }
      built.push({ kind: 'msg', ...m });
    });
    items.value = built;
  } catch (e) {
    items.value = [];
  } finally {
    emit('loaded');
  }
};

watch(() => props.conversationId, load, { immediate: true });
</script>

<template>
  <li v-if="items.length" class="list-none block">
    <div class="flex flex-col gap-2 pt-2 pb-1">
      <template v-for="it in items" :key="it.id || it.kind + it.created_at + it.content">
        <div
          v-if="it.kind === 'sep'"
          class="self-center my-2 text-[10px] font-bold tracking-wide uppercase text-n-slate-10 flex items-center gap-2 before:content-[''] before:h-px before:w-12 before:bg-white/10 after:content-[''] after:h-px after:w-12 after:bg-white/10"
        >
          conversación · {{ it.label }}
        </div>
        <div v-else class="flex" :class="isOutgoing(it) ? 'justify-end' : 'justify-start'">
          <div
            class="max-w-[72%] rounded-2xl px-3 py-2 text-sm"
            :class="
              it.private
                ? 'bg-[#19191f] border-l-[3px] border-l-[#c9a35a] text-[#e7e7ea]'
                : isOutgoing(it)
                  ? 'bg-[#0e3b2a] border border-[rgba(78,199,125,.25)] text-white'
                  : 'bg-n-slate-3 text-n-slate-12'
            "
          >
            <div v-if="it.content" class="whitespace-pre-wrap break-words">
              {{ it.content }}
            </div>
            <div v-for="a in it.attachments || []" :key="a.id" class="mt-1.5">
              <img
                v-if="a.file_type === 'image'"
                :src="a.data_url"
                class="rounded-lg max-h-52 max-w-full"
              />
              <audio
                v-else-if="a.file_type === 'audio'"
                :src="a.data_url"
                controls
                class="w-52"
              />
              <video
                v-else-if="a.file_type === 'video'"
                :src="a.data_url"
                controls
                class="rounded-lg max-h-52 max-w-full"
              />
              <a
                v-else
                :href="a.data_url"
                target="_blank"
                class="text-xs underline opacity-90"
              >
                📎 Adjunto
              </a>
            </div>
            <div
              class="text-[10px] mt-1 opacity-50"
              :class="isOutgoing(it) ? 'text-right' : ''"
            >
              {{ fmtTime(it.created_at) }}
            </div>
          </div>
        </div>
      </template>
      <div
        class="self-center my-2 text-[10px] font-bold tracking-wide uppercase text-n-slate-10 flex items-center gap-2 before:content-[''] before:h-px before:w-12 before:bg-white/10 after:content-[''] after:h-px after:w-12 after:bg-white/10"
      >
        conversación · hoy
      </div>
    </div>
  </li>
</template>
